import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { LogoStacked } from '../components/Logo';

/**
 * Extract an invite token from either a full URL (https://app.ourcove.space/invite/TOKEN)
 * or a bare token pasted on its own. Returns null if we can't recognise one.
 */
function parseInviteToken(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const m = s.match(/\/invite\/([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  // Bare token: at least 12 url-safe chars
  if (/^[A-Za-z0-9_-]{12,}$/.test(s)) return s;
  return null;
}

export function Login() {
  const nav = useNavigate();
  const setAuth = useStore((s) => s.setAuth);
  const [name, setName] = useState('');
  const [coveName, setCoveName] = useState('');
  const [needsCoveName, setNeedsCoveName] = useState(false);
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteInput, setInviteInput] = useState('');
  const [inviteErr, setInviteErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const trimmed = name.trim();
      const looksLikeEmail = trimmed.includes('@');
      const r = await api.login(
        looksLikeEmail
          ? { email: trimmed.toLowerCase(), password: pw }
          : { displayName: trimmed, coveName: coveName.trim() || undefined, password: pw },
      );
      setAuth(r.accessToken, r.user, r.cove);
      nav('/');
    } catch (e: any) {
      if (e?.status === 409) {
        setNeedsCoveName(true);
        setErr('Multiple coves have that name — please include your cove name.');
      } else {
        setErr(e.message || 'login failed');
      }
    } finally {
      setBusy(false);
    }
  }

  function openInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteErr(null);
    const token = parseInviteToken(inviteInput);
    if (!token) {
      setInviteErr("That doesn't look like an invite link.");
      return;
    }
    nav(`/invite/${token}`);
  }

  return (
    <div className="app">
      <div className="auth-hero">
        <LogoStacked />
        <h1 className="display auth-title">Welcome back.</h1>
        <p className="muted auth-sub">Only your family can see you here.</p>

        <form className="auth-form" onSubmit={submit}>
          <label className="field">
            <span className="field-label">Your name or email</span>
            <input
              className="input"
              placeholder="Ana Alvarez or you@example.com"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoCapitalize="none"
              autoComplete="username"
            />
          </label>
          {needsCoveName && (
            <label className="field">
              <span className="field-label">Your cove name</span>
              <input
                className="input"
                placeholder="The Alvarez family"
                value={coveName}
                onChange={(e) => setCoveName(e.target.value)}
                autoCapitalize="none"
              />
            </label>
          )}
          <label className="field">
            <span className="field-label">Password</span>
            <div className="input-pw">
              <input
                className="input"
                type={showPw ? 'text' : 'password'}
                placeholder="••••••••"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="input-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>
          {err && <div className="error">{err}</div>}
          <button className="btn block" type="submit" disabled={busy || !name || pw.length < 6}>
            {busy ? 'Signing in…' : 'Sign in to your cove'}
          </button>
        </form>

        <div className="or-divider"><span>or</span></div>

        {!inviteOpen ? (
          <button
            type="button"
            className="invite-link-btn"
            onClick={() => setInviteOpen(true)}
          >
            Have an invite link? <strong>Paste it to join →</strong>
          </button>
        ) : (
          <form className="auth-form" onSubmit={openInvite}>
            <label className="field">
              <span className="field-label">Your invite link</span>
              <input
                className="input"
                placeholder="https://app.ourcove.space/invite/…"
                value={inviteInput}
                onChange={(e) => { setInviteInput(e.target.value); setInviteErr(null); }}
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </label>
            {inviteErr && <div className="error">{inviteErr}</div>}
            <button className="btn block" type="submit" disabled={!inviteInput.trim()}>
              Open invite
            </button>
            <button
              type="button"
              className="btn secondary block"
              onClick={() => { setInviteOpen(false); setInviteInput(''); setInviteErr(null); }}
            >
              Cancel
            </button>
          </form>
        )}

        <p className="auth-footnote">
          No accounts without an invite.<br />
          No public directory.
        </p>
      </div>
    </div>
  );
}
